/* ============================================================
   BED BUILDER — JavaScript
   Littil Shopify Custom Page
   ============================================================ */

/* ============================================================
   CONFIG — Adjust these values without touching any other code
   ============================================================ */
const BB_CONFIG = {
  // Discount: overridden by window.BB_DISCOUNT_SETTINGS in config file
  DISCOUNT_PER_ITEM_PERCENT: 3,
  DISCOUNT_THRESHOLD:        2,

  // Discount codes must exist in Shopify Admin → Discounts
  // Naming convention: PREFIX + percent (e.g. BUNDLE3, BUNDLE6)
  DISCOUNT_CODE_PREFIX: 'BUNDLE',

  // Delay before INSPO popup auto-appears (ms)
  INSPO_DELAY_MS: 30000,
  INSPO_URL: '/pages/inspired-looks',

  // Currency symbol shown throughout
  CURRENCY: 'RM',

  // Sizes — overridden by window.BB_SIZES in config file
  SIZES: {
    single: { label: 'Single', dimensions: '' },
    queen:  { label: 'Queen',  dimensions: '' },
    king:   { label: 'King',   dimensions: '' },
  },

  // Single fabric — only one option
  FABRICS: {
    tencel: { label: 'Littil TENCEL™ AIR Blend', shortLabel: 'TENCEL™ AIR Blend' },
  },
}

/* ============================================================
   PRODUCT DATA
   ─────────────────────────────────────────────────────────────
   For each color, set:
     variantIds: { '<size>_<fabric>': '<shopify_variant_id>' }
     layerImage: full URL to the PNG overlay for that colour

   Variant key format: queen_tencel, king_cotton, single_linen …
   Leave variantIds empty strings as placeholders; replace before
   going live.
   ============================================================ */
/* ============================================================
   COLOURS — shared across all products.
   swatch      = hex shown in the colour dot (update to match your brand)
   swatchType  = 'stripe' uses two colours instead of one solid
   ============================================================ */
const COLOURS = [
  {
    id: 'pyjama_stripes_oak',
    label: 'Pyjama Stripes in Oak',
    swatchType: 'stripe',
    swatchColors: ['#C9A87C', '#F5EDD7'],  // ← update hex to match real colours
  },
  {
    id: 'sleepover_stripes_blue',
    label: 'Sleepover Stripes in Blue',
    swatchType: 'stripe',
    swatchColors: ['#6B9EC4', '#EEF4FA'],  // ← update hex to match real colours
  },
  {
    id: 'pyjama_stripes_forest',
    label: 'Pyjama Stripes in Forest',
    swatchType: 'stripe',
    swatchColors: ['#3D6B52', '#EEF5EE'],  // ← update hex to match real colours
  },
  {
    id: 'agnes_pink',
    label: "Agnes' Pink",
    swatch: '#E8A0A4',                     // ← update hex to match real colour
  },
  {
    id: 'canvas_beige',
    label: 'Canvas Beige',
    swatch: '#D4C4A8',                     // ← update hex to match real colour
  },
  {
    id: 'silver_blue',
    label: 'Silver Blue',
    swatch: '#9BB5C8',                     // ← update hex to match real colour
  },
  {
    id: 'candy_stripes_pink',
    label: 'Candy Stripes in Pink',
    swatchType: 'stripe',
    swatchColors: ['#F2A7C3', '#FEF0F5'],  // ← update hex to match real colours
    comingSoon: true,                       // ← set to false to launch
  },
]

/* ============================================================
   Helper — builds a colour entry for a product.
   Fill in variantIds and layerImage for each product × colour.
   ============================================================ */
function makeColor(id, productId) {
  const base = COLOURS.find(c => c.id === id)
  if (!base) return null
  if (base.comingSoon) return null

  // Pull image URL and variant IDs from the config file (bed-builder-config.js)
  const layerImage = window.BB_IMAGES?.[productId]?.[id] || ''
  const variantIds = window.BB_VARIANTS?.[productId]?.[id] || {}

  return { ...base, variantIds, layerImage }
}

// Reads the add-on discount % for a product from bed-builder-config.js
function addonPct(productId, fallback = 20) {
  return window.BB_DISCOUNT_SETTINGS?.addonDiscounts?.[productId] ?? fallback
}

const BB_PRODUCTS = [
  {
    id: 'fitted_sheet',
    title: 'Fitted Sheet',
    isAddon: false,
    addonDiscount: 0,
    colors: [
      makeColor('pyjama_stripes_oak',     'fitted_sheet'),
      makeColor('sleepover_stripes_blue', 'fitted_sheet'),
      makeColor('pyjama_stripes_forest',  'fitted_sheet'),
      makeColor('agnes_pink',             'fitted_sheet'),
      makeColor('canvas_beige',           'fitted_sheet'),
      makeColor('silver_blue',            'fitted_sheet'),
      makeColor('candy_stripes_pink',     'fitted_sheet'),
    ].filter(Boolean),
  },
  {
    id: 'pillowcase_pair',
    title: 'Pillowcase Pair',
    isAddon: false,
    addonDiscount: 0,
    colors: [
      makeColor('pyjama_stripes_oak',     'pillowcase_pair'),
      makeColor('sleepover_stripes_blue', 'pillowcase_pair'),
      makeColor('pyjama_stripes_forest',  'pillowcase_pair'),
      makeColor('agnes_pink',             'pillowcase_pair'),
      makeColor('canvas_beige',           'pillowcase_pair'),
      makeColor('silver_blue',            'pillowcase_pair'),
      makeColor('candy_stripes_pink',     'pillowcase_pair'),
    ].filter(Boolean),
  },
  {
    id: 'duvet_cover',
    title: 'Duvet Cover',
    isAddon: false,
    addonDiscount: 0,
    colors: [
      makeColor('pyjama_stripes_oak',     'duvet_cover'),
      makeColor('sleepover_stripes_blue', 'duvet_cover'),
      makeColor('pyjama_stripes_forest',  'duvet_cover'),
      makeColor('agnes_pink',             'duvet_cover'),
      makeColor('canvas_beige',           'duvet_cover'),
      makeColor('silver_blue',            'duvet_cover'),
      makeColor('candy_stripes_pink',     'duvet_cover'),
    ].filter(Boolean),
  },
  {
    id: 'bolstercase',
    title: '+Bolstercase',
    isAddon: true,
    addonDiscount: addonPct('bolstercase'),
    colors: [
      makeColor('pyjama_stripes_oak',     'bolstercase'),
      makeColor('sleepover_stripes_blue', 'bolstercase'),
      makeColor('pyjama_stripes_forest',  'bolstercase'),
      makeColor('agnes_pink',             'bolstercase'),
      makeColor('canvas_beige',           'bolstercase'),
      makeColor('silver_blue',            'bolstercase'),
      makeColor('candy_stripes_pink',     'bolstercase'),
    ].filter(Boolean),
  },
  {
    id: 'pillow_case',
    title: '+Pillow Case',
    isAddon: true,
    addonDiscount: addonPct('pillow_case'),
    colors: [
      makeColor('pyjama_stripes_oak',     'pillow_case'),
      makeColor('sleepover_stripes_blue', 'pillow_case'),
      makeColor('pyjama_stripes_forest',  'pillow_case'),
      makeColor('agnes_pink',             'pillow_case'),
      makeColor('canvas_beige',           'pillow_case'),
      makeColor('silver_blue',            'pillow_case'),
      makeColor('candy_stripes_pink',     'pillow_case'),
    ].filter(Boolean),
  },
]

/* ============================================================
   STATE
   ============================================================ */
const state = {
  step: 1,
  size: null,
  fabric: null,
  // Confirmed selections: { item_id: { colorId, variantId, layerImage, label } }
  selections: {},
  // Pending (in-panel, not yet confirmed): { item_id: { ... } }
  pending: {},
  // Per-item size override — defaults to `size` above unless changed in a drawer
  itemSizes: {},
  expandedItem: null,
  inspoShown: false,
  inspoTimer: null,
}

// Returns the active size for one item — its own override if set, else the global Step 1 choice
function getItemSize(itemId) {
  return state.itemSizes[itemId] || state.size
}

/* ============================================================
   BOOT
   ============================================================ */
document.addEventListener('DOMContentLoaded', () => {
  // Merge config sizes into BB_CONFIG
  if (window.BB_SIZES) Object.assign(BB_CONFIG.SIZES, window.BB_SIZES)

  // Merge discount settings into BB_CONFIG
  if (window.BB_DISCOUNT_SETTINGS) {
    BB_CONFIG.DISCOUNT_PER_ITEM_PERCENT = window.BB_DISCOUNT_SETTINGS.discountPerItemPercent ?? BB_CONFIG.DISCOUNT_PER_ITEM_PERCENT
    BB_CONFIG.DISCOUNT_THRESHOLD        = window.BB_DISCOUNT_SETTINGS.discountThreshold ?? BB_CONFIG.DISCOUNT_THRESHOLD
  }

  // Populate size cards from config
  populateSizeCards()

  // Set all image srcs from config
  loadImagesFromConfig()

  buildItems()
  initStepButtons()
  initZoom()
  initInspoBtn()
  initInspoCarousel()
  initHowItWorks()
  initSummary()
  initAddToBag()

  // Start on step 1 with builder hidden
  goToStep(1)
})

function populateSizeCards() {
  document.querySelectorAll('#bb-size-grid .bb-option-card').forEach(card => {
    const key  = card.dataset.value
    const size = BB_CONFIG.SIZES[key]
    if (!size) return
    const titleEl = card.querySelector('.bb-option-card__title')
    const subEl   = card.querySelector('.bb-option-card__subtitle')
    if (titleEl) titleEl.textContent = size.label
    if (subEl)   subEl.textContent   = size.dimensions
  })
}

function loadImagesFromConfig() {
  const imgs = window.BB_IMAGES || {}

  // Base
  const base = document.getElementById('bb-layer-base')
  if (base && imgs.base) base.src = imgs.base

  // Default grey layers
  const defs = imgs.defaults || {}
  ;['pillowcase_pair', 'duvet_cover', 'bolstercase', 'pillow_case'].forEach(id => {
    const el = document.getElementById(`bb-layer-${id}-default`)
    if (el && defs[id]) el.src = defs[id]
  })
}

/* ============================================================
   STEP NAVIGATION
   ============================================================ */
function initStepButtons() {
  // Step 1 — size selection
  document.querySelectorAll('#bb-size-grid .bb-option-card').forEach(card => {
    card.addEventListener('click', () => {
      document.querySelectorAll('#bb-size-grid .bb-option-card').forEach(c => c.classList.remove('selected'))
      card.classList.add('selected')
      state.size = card.dataset.value
      document.getElementById('bb-step1-btn').disabled = false
    })
  })

  document.getElementById('bb-step1-btn').addEventListener('click', () => {
    if (state.size) goToStep(2)
  })

  // Step 2 — informational only, fabric auto-set to tencel
  document.getElementById('bb-step2-btn').addEventListener('click', () => {
    state.fabric = 'tencel'
    goToStep(3)
  })

  // Back buttons
  document.getElementById('bb-back-to-1').addEventListener('click', () => goToStep(1))
  document.getElementById('bb-back-to-2').addEventListener('click', () => goToStep(2))
}

function goToStep(n) {
  state.step = n

  document.getElementById('bb-step-1').classList.toggle('hidden', n !== 1)
  document.getElementById('bb-step-2').classList.toggle('hidden', n !== 2)

  const builder = document.getElementById('bb-builder')

  if (n === 3) {
    builder.style.display = 'block'
    document.body.classList.remove('bb-on-step')
    syncSizeFabricLabels()
    renderPricing()
    renderProgressBar()
    startInspoTimer()
  } else {
    builder.style.display = 'none'
    document.body.classList.add('bb-on-step')
    clearTimeout(state.inspoTimer)
  }

  window.scrollTo({ top: 0, behavior: 'smooth' })
}

/* ============================================================
   BUILD ITEM ROWS (once on DOMContentLoaded)
   ============================================================ */
function buildItems() {
  const container = document.getElementById('bb-items')
  container.innerHTML = ''

  BB_PRODUCTS.forEach(product => {
    const itemEl = document.createElement('div')
    itemEl.className = 'bb-item' + (product.isAddon ? ' bb-item--addon' : '')
    itemEl.dataset.itemId = product.id

    itemEl.innerHTML = `
      <div class="bb-item__header" role="button" tabindex="0" aria-expanded="false">
        <span class="bb-item__dot" id="dot-${product.id}"></span>
        <span class="bb-item__name">${product.title}</span>
        <span class="bb-item__meta" id="meta-${product.id}">
          ${product.isAddon
            ? `<button class="bb-addon-btn" data-addon="${product.id}" type="button">ADD ON FOR ${product.addonDiscount}% OFF</button>`
            : ''}
        </span>
        <span class="bb-item__price-tag" id="price-${product.id}"></span>
        <span class="bb-item__chevron" aria-hidden="true">&#8964;</span>
        <span class="bb-item__check" id="check-${product.id}" aria-hidden="true">
          <svg viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <polyline points="1,4 3.5,6.5 9,1" stroke="white" stroke-width="2.2"
              stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      </div>
      <div class="bb-item__panel" id="panel-${product.id}" role="region" aria-hidden="true">
        <div class="bb-panel-meta">
          Material: <strong id="mat-${product.id}">—</strong>
          &nbsp;&nbsp;Size:
          <select class="bb-size-select" id="size-select-${product.id}" data-item="${product.id}"></select>
        </div>
        <div class="bb-swatch-label">Choose Colour</div>
        <div class="bb-swatches" id="swatches-${product.id}"></div>
        <div class="bb-panel-shipinfo" id="shipinfo-${product.id}"></div>
        <button class="bb-confirm-btn" type="button" data-confirm="${product.id}">CONFIRM</button>
        <button class="bb-remove-link" type="button" data-remove="${product.id}">
          <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 3.5h10M5.5 3.5V2.5a.5.5 0 01.5-.5h2a.5.5 0 01.5.5v1M5 3.5l.5 7M9 3.5l-.5 7M3.5 3.5l.5 7.5a.5.5 0 00.5.5h5a.5.5 0 00.5-.5l.5-7.5"
              stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Remove Item From Bundle
        </button>
      </div>
    `

    container.appendChild(itemEl)
    buildSwatches(product)

    // Click header → toggle expand / focus
    itemEl.querySelector('.bb-item__header').addEventListener('click', e => {
      if (e.target.closest('.bb-addon-btn')) return
      toggleItem(product.id)
    })

    // Addon button also opens the panel
    const addonBtn = itemEl.querySelector('.bb-addon-btn')
    if (addonBtn) {
      addonBtn.addEventListener('click', e => {
        e.stopPropagation()
        toggleItem(product.id)
      })
    }

    // Per-item size dropdown
    const sizeSelect = itemEl.querySelector(`#size-select-${product.id}`)
    if (sizeSelect) {
      sizeSelect.addEventListener('click', e => e.stopPropagation())
      sizeSelect.addEventListener('change', e => onItemSizeChange(product.id, e.target.value))
    }
  })

  // Confirm / Remove (event delegation on container)
  container.addEventListener('click', e => {
    const confirmBtn = e.target.closest('[data-confirm]')
    const removeBtn  = e.target.closest('[data-remove]')
    if (confirmBtn) confirmItem(confirmBtn.dataset.confirm)
    if (removeBtn)  removeItem(removeBtn.dataset.remove)
  })
}

// Fills each item's size dropdown with the available sizes, defaulting to Step 1's choice
function populateItemSizeDropdown(itemId) {
  const select = document.getElementById(`size-select-${itemId}`)
  if (!select) return

  select.innerHTML = Object.entries(BB_CONFIG.SIZES)
    .map(([key, s]) => `<option value="${key}">${s.label}</option>`)
    .join('')

  select.value = getItemSize(itemId)
}

function onItemSizeChange(itemId, newSize) {
  state.itemSizes[itemId] = newSize

  const product = BB_PRODUCTS.find(p => p.id === itemId)

  // Recompute variant ID for the current colour selection (pending or confirmed)
  const sel = state.pending[itemId] || state.selections[itemId]
  if (sel) {
    const color = product?.colors.find(c => c.id === sel.colorId)
    if (color) {
      const variantKey = `${newSize}_${state.fabric}`
      sel.variantId = color.variantIds?.[variantKey] || Object.values(color.variantIds || {}).find(Boolean) || ''
    }
  }

  if (product) updateSwatchAvailability(product)
  renderItemPrice(itemId)
  if (state.selections[itemId]) renderPricing()
}

function buildSwatches(product) {
  const wrap = document.getElementById(`swatches-${product.id}`)
  if (!wrap) return
  wrap.innerHTML = ''

  product.colors.filter(Boolean).forEach(color => {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'bb-swatch'
    btn.dataset.colorId = color.id

    // Use swatch image from config if available
    const swatchImg = window.BB_SWATCH_IMAGES?.[color.id]
    if (swatchImg) {
      const img = document.createElement('img')
      img.src = swatchImg
      img.alt = color.label
      btn.appendChild(img)
    } else if (color.swatchType === 'stripe') {
      const [c1, c2] = color.swatchColors
      btn.style.backgroundImage =
        `repeating-linear-gradient(45deg, ${c1} 0, ${c1} 4px, ${c2} 4px, ${c2} 8px)`
    } else {
      btn.style.background = color.swatch
    }

    btn.addEventListener('click', () => {
      if (btn.classList.contains('sold-out')) return
      selectColor(product.id, color.id)
    })
    wrap.appendChild(btn)
  })

  updateSwatchAvailability(product)
}

// Disables + crosses out swatches that are SOLD OUT at the item's current size
function updateSwatchAvailability(product) {
  const wrap = document.getElementById(`swatches-${product.id}`)
  if (!wrap) return

  product.colors.filter(Boolean).forEach(color => {
    const btn = wrap.querySelector(`.bb-swatch[data-color-id="${color.id}"]`)
    if (!btn) return

    const info   = getItemPricing(product.id, color.id)
    const status = getAvailabilityStatus(info, color.id)
    const isSoldOut = status === 'soldout'

    btn.classList.toggle('sold-out', isSoldOut)
    btn.disabled = isSoldOut
    btn.setAttribute('aria-label', isSoldOut ? `${color.label} — Sold Out` : color.label)
    btn.title = isSoldOut ? `${color.label} — Sold Out` : color.label
  })
}

/* ============================================================
   FOCUS / EXPAND MODE
   ============================================================ */
function toggleItem(itemId) {
  const isExpanding = state.expandedItem !== itemId
  collapseAll()
  if (isExpanding) expandItem(itemId)
}

function expandItem(itemId) {
  state.expandedItem = itemId

  const itemEl = document.querySelector(`[data-item-id="${itemId}"]`)
  itemEl.classList.add('expanded')
  itemEl.querySelector('.bb-item__header').setAttribute('aria-expanded', 'true')
  itemEl.querySelector('.bb-item__panel').setAttribute('aria-hidden', 'false')

  // Focus mode: hide every other item
  document.querySelectorAll('.bb-item').forEach(el => {
    if (el.dataset.itemId !== itemId) el.classList.add('focus-hidden')
  })

  document.getElementById('bb-hint-top').style.display = 'none'

  // Reflect existing pending/confirmed swatch selection
  const sel = state.pending[itemId] || state.selections[itemId]
  if (sel) highlightSwatch(itemId, sel.colorId)

  renderItemPrice(itemId)

  itemEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
}

/* ============================================================
   PRICING, STOCK & PREORDER — per item, per colour, per size
   ============================================================ */

// Only these colours show PREORDER when out of stock.
// Any other out-of-stock colour shows SOLD OUT instead (disabled swatch).
const PREORDER_ALLOWED_COLOURS = ['pyjama_stripes_oak', 'sleepover_stripes_blue', 'canvas_beige']

function getItemPricing(itemId, colorId) {
  const fallback = { price: 0, compareAt: '', available: true, shipDate: '' }
  if (!colorId) return fallback
  return window.BB_PRICING?.[itemId]?.[colorId]?.[getItemSize(itemId)] || fallback
}

// Returns 'instock' | 'preorder' | 'soldout'
function getAvailabilityStatus(info, colorId) {
  if (info.available) return 'instock'
  return PREORDER_ALLOWED_COLOURS.includes(colorId) ? 'preorder' : 'soldout'
}

function renderItemPrice(itemId) {
  const el       = document.getElementById(`price-${itemId}`)
  const shipEl   = document.getElementById(`shipinfo-${itemId}`)
  if (!el) return

  const sel    = state.pending[itemId] || state.selections[itemId]
  const cur    = BB_CONFIG.CURRENCY
  const info   = getItemPricing(itemId, sel?.colorId)
  const status = getAvailabilityStatus(info, sel?.colorId)

  if (!info.price) {
    el.innerHTML = ''
    if (shipEl) shipEl.textContent = ''
    return
  }

  const hasDiscount = info.compareAt && Number(info.compareAt) > Number(info.price)

  // Header price tag — compact, wraps if needed. Order: [Preorder/SoldOut badge] then [Price]
  if (status === 'soldout') {
    el.innerHTML = `<span class="bb-soldout-badge">SOLD OUT</span>`
  } else if (status === 'preorder') {
    el.innerHTML = `
      <span class="bb-preorder-badge">PREORDER</span>
      ${info.compareAt ? `<span class="bb-price-compare">${cur}${info.compareAt}</span>` : ''}
      <span class="bb-price-current-inline">${cur}${info.price}</span>`
  } else if (hasDiscount) {
    el.innerHTML = `
      <span class="bb-price-compare">${cur}${info.compareAt}</span>
      <span class="bb-price-current-inline">${cur}${info.price}</span>`
  } else {
    el.innerHTML = `<span class="bb-price-current-inline">${cur}${info.price}</span>`
  }

  // Shipping detail — only shown in the expanded panel, only for preorder
  if (shipEl) {
    shipEl.textContent = (status === 'preorder' && info.shipDate) ? info.shipDate : ''
  }
}

function collapseAll() {
  if (state.expandedItem) {
    const itemEl = document.querySelector(`[data-item-id="${state.expandedItem}"]`)
    if (itemEl) {
      itemEl.classList.remove('expanded')
      itemEl.querySelector('.bb-item__header').setAttribute('aria-expanded', 'false')
      itemEl.querySelector('.bb-item__panel').setAttribute('aria-hidden', 'true')
    }
  }

  document.querySelectorAll('.bb-item').forEach(el => el.classList.remove('focus-hidden'))
  document.getElementById('bb-hint-top').style.display = ''
  state.expandedItem = null
}

/* ============================================================
   COLOR SELECTION
   ============================================================ */
function selectColor(itemId, colorId) {
  const product = BB_PRODUCTS.find(p => p.id === itemId)
  const color   = product?.colors.find(c => c.id === colorId)
  if (!color) return

  const variantKey = `${getItemSize(itemId)}_${state.fabric}`
  const variantId  = color.variantIds?.[variantKey]
    || Object.values(color.variantIds || {}).find(Boolean)
    || ''

  state.pending[itemId] = { colorId, variantId, layerImage: color.layerImage, label: color.label }

  highlightSwatch(itemId, colorId)
  showLayer(itemId, color.layerImage) // live preview
  renderItemPrice(itemId)
}

function highlightSwatch(itemId, colorId) {
  document.querySelectorAll(`#swatches-${itemId} .bb-swatch`).forEach(s => {
    s.classList.toggle('selected', s.dataset.colorId === colorId)
  })
}

/* ============================================================
   CONFIRM / REMOVE
   ============================================================ */
function confirmItem(itemId) {
  const pending = state.pending[itemId]

  if (!pending) {
    // No colour chosen — just collapse
    collapseAll()
    return
  }

  state.selections[itemId] = pending
  delete state.pending[itemId]

  updateRowUI(itemId, true)
  showLayer(itemId, pending.layerImage)
  updateGreyDefaultsVisibility()
  renderItemPrice(itemId)

  renderPricing()
  renderProgressBar()
  maybeShowDiscountToast()
  collapseAll()
}

function removeItem(itemId) {
  delete state.selections[itemId]
  delete state.pending[itemId]

  updateRowUI(itemId, false)
  hideLayer(itemId)
  updateGreyDefaultsVisibility()
  document.getElementById(`price-${itemId}`)?.replaceChildren()
  const shipEl = document.getElementById(`shipinfo-${itemId}`)
  if (shipEl) shipEl.textContent = ''

  renderPricing()
  renderProgressBar()
  collapseAll()
}

// All grey "not selected yet" placeholder layer IDs
const GREY_DEFAULT_LAYER_IDS = [
  'base',
  'pillowcase_pair-default',
  'duvet_cover-default',
  'bolstercase-default',
  'pillow_case-default',
]

// As soon as ANY item is selected, hide ALL grey placeholders at once —
// leaving only the actually-confirmed coloured layers visible.
// With zero items selected, show the full grey "bed" again.
function updateGreyDefaultsVisibility() {
  const anySelected = getSelectedCount() > 0
  GREY_DEFAULT_LAYER_IDS.forEach(layerId => {
    document.getElementById(`bb-layer-${layerId}`)?.classList.toggle('visible', !anySelected)
  })
}

function updateRowUI(itemId, isSelected) {
  const product = BB_PRODUCTS.find(p => p.id === itemId)
  const sel     = state.selections[itemId]

  const dot      = document.getElementById(`dot-${itemId}`)
  const check    = document.getElementById(`check-${itemId}`)
  const metaEl   = document.getElementById(`meta-${itemId}`)
  const addonBtn = metaEl?.querySelector('.bb-addon-btn')

  dot?.classList.toggle('active', isSelected)
  check?.classList.toggle('checked', isSelected)

  if (!product.isAddon && metaEl) {
    if (isSelected && sel) {
      const swatchColor = getSwatchColor(product, sel.colorId)
      const fabricShort = BB_CONFIG.FABRICS[state.fabric]?.shortLabel || state.fabric
      metaEl.innerHTML = `
        <span class="bb-item__meta-dot" style="background:${swatchColor}"></span>
        <span>${fabricShort}</span>`
    } else {
      metaEl.innerHTML = ''
    }
  }

  if (product.isAddon && addonBtn) {
    addonBtn.style.display = isSelected ? 'none' : ''
  }
}

function getSwatchColor(product, colorId) {
  const c = product.colors.find(col => col.id === colorId)
  return c?.swatch || c?.swatchColors?.[0] || '#ccc'
}

/* ============================================================
   IMAGE LAYERS
   ============================================================ */
function showLayer(itemId, src) {
  const layerEl = document.getElementById(`bb-layer-${itemId}`)
  if (!layerEl) return
  if (src) layerEl.src = src
  layerEl.classList.add('visible')
}

function hideLayer(itemId) {
  const layerEl = document.getElementById(`bb-layer-${itemId}`)
  layerEl?.classList.remove('visible')
}

/* ============================================================
   PRICING & DISCOUNT
   ============================================================ */
function getSelectedCount() {
  return BB_PRODUCTS.filter(p => state.selections[p.id]).length
}

function getDiscountPercent() {
  const n = getSelectedCount()
  if (n < BB_CONFIG.DISCOUNT_THRESHOLD) return 0
  return (n - 1) * BB_CONFIG.DISCOUNT_PER_ITEM_PERCENT
}

function getDiscountCode() {
  const pct = getDiscountPercent()
  return pct ? `${BB_CONFIG.DISCOUNT_CODE_PREFIX}${pct}` : null
}

// Sums real per-item prices from BB_PRICING for everything currently selected
function calculateBundleTotals() {
  let originalTotal = 0  // sum of "full price" (compareAt if set, else price)
  let subtotal       = 0  // sum of actual selling prices (after any addon discount)

  BB_PRODUCTS.forEach(product => {
    const sel = state.selections[product.id]
    if (!sel) return

    const info = getItemPricing(product.id, sel.colorId)
    let itemPrice = info.price

    if (product.isAddon && product.addonDiscount) {
      itemPrice = itemPrice * (1 - product.addonDiscount / 100)
    }

    originalTotal += info.compareAt ? Number(info.compareAt) : info.price
    subtotal      += itemPrice
  })

  return { originalTotal, subtotal }
}

function renderPricing() {
  const cur = BB_CONFIG.CURRENCY
  const { originalTotal, subtotal } = calculateBundleTotals()
  const discountPct  = getDiscountPercent()
  const finalTotal   = Math.round(subtotal * (1 - discountPct / 100))
  const totalSavePct = originalTotal > 0 ? Math.round((1 - finalTotal / originalTotal) * 100) : 0

  const priceEl     = document.getElementById('bb-price-current')
  const saveEl      = document.getElementById('bb-price-save')
  const originalEl  = document.getElementById('bb-price-original')
  const hintEl       = document.getElementById('bb-hint-top')
  const hintBot       = document.getElementById('bb-hint-bottom')

  if (priceEl) priceEl.textContent = `${cur}${finalTotal}`

  if (originalEl) {
    originalEl.style.display = totalSavePct > 0 ? '' : 'none'
    originalEl.textContent   = `${cur}${Math.round(originalTotal)}`
  }
  if (saveEl) {
    saveEl.style.display = totalSavePct > 0 ? '' : 'none'
    saveEl.textContent   = `SAVE ${totalSavePct}%`
  }

  const n     = getSelectedCount()
  const left  = BB_CONFIG.DISCOUNT_THRESHOLD - n
  const hintMsg = discountPct > 0
    ? `${discountPct}% bundle discount applied! Add more items to save more.`
    : left > 0
      ? `Choose ${left} more item${left > 1 ? 's' : ''} to activate set discount. <a href="#" class="js-how-it-works">How it works</a>`
      : `Choose ${BB_CONFIG.DISCOUNT_THRESHOLD} items or more to activate set discount. <a href="#" class="js-how-it-works">How it works</a>`

  if (hintEl)  hintEl.innerHTML  = hintMsg
  if (hintBot) hintBot.innerHTML = hintMsg

  // Re-bind how-it-works links (innerHTML replaced them)
  document.querySelectorAll('.js-how-it-works').forEach(a => {
    a.addEventListener('click', e => { e.preventDefault(); openModal() })
  })
}

function maybeShowDiscountToast() {
  const n   = getSelectedCount()
  const pct = getDiscountPercent()

  if (pct > 0) {
    const msg = n === BB_CONFIG.DISCOUNT_THRESHOLD
      ? `${pct}% bundle discount activated!`
      : `${pct}% bundle discount — keep going!`
    showToast(msg)
  }
}

function showToast(msg) {
  let t = document.querySelector('.bb-discount-toast')
  if (!t) {
    t = document.createElement('div')
    t.className = 'bb-discount-toast'
    document.body.appendChild(t)
  }
  t.textContent = msg
  t.classList.add('visible')
  clearTimeout(t._timer)
  t._timer = setTimeout(() => t.classList.remove('visible'), 3000)
}

/* ============================================================
   PROGRESS BAR — persistent in footer
   ============================================================ */
function renderProgressBar() {
  const count     = getSelectedCount()
  const maxItems  = BB_PRODUCTS.length
  const perItem   = BB_CONFIG.DISCOUNT_PER_ITEM_PERCENT
  const threshold = BB_CONFIG.DISCOUNT_THRESHOLD
  const pct       = getDiscountPercent()
  const fillPct   = Math.round((count / maxItems) * 100)

  const fillEl    = document.getElementById('bb-progress-fill')
  const labelEl   = document.getElementById('bb-progress-label')
  const discEl    = document.getElementById('bb-progress-discount')
  const nextEl    = document.getElementById('bb-progress-next')

  if (fillEl)  fillEl.style.width = `${fillPct}%`

  if (count === 0) {
    if (labelEl) labelEl.textContent = 'Select items to build your bundle'
    if (discEl)  discEl.textContent  = ''
    if (nextEl)  nextEl.textContent  = ''
    return
  }

  if (labelEl) labelEl.textContent = `${count} of ${maxItems} items selected`
  if (discEl)  discEl.textContent  = pct > 0 ? `${pct}% off` : ''

  if (count < maxItems) {
    const nextPct = count >= threshold
      ? (count) * perItem
      : threshold > count ? (threshold - 1) * perItem : 0
    if (nextEl && nextPct > 0) {
      nextEl.textContent = `Add ${threshold > count ? threshold - count : 1} more item${threshold - count > 1 ? 's' : ''} to unlock ${nextPct}% off`
    } else if (nextEl && count < threshold) {
      nextEl.textContent = `Add ${threshold - count} more to unlock your first discount`
    } else if (nextEl) {
      nextEl.textContent = ''
    }
  } else {
    if (nextEl) nextEl.textContent = '🎉 You\'ve built the full set!'
  }
}

/* ============================================================
   INSPO CAROUSEL
   ============================================================ */
let inspoIndex = 0

function initInspoCarousel() {
  const overlay   = document.getElementById('bb-inspo-modal-overlay')
  const closeBtn  = document.getElementById('bb-inspo-modal-close')
  const track     = document.getElementById('bb-inspo-track')
  const dotsWrap  = document.getElementById('bb-inspo-dots')
  const looks     = window.BB_INSPO_LOOKS || []

  if (!track || looks.length === 0) return

  // Build slides
  looks.forEach((look, i) => {
    const slide = document.createElement('div')
    slide.className = 'bb-inspo-slide'
    slide.innerHTML = look.image
      ? `<img src="${look.image}" alt="${look.caption}" loading="lazy">
         <div class="bb-inspo-slide__caption">${look.caption}</div>`
      : `<div style="aspect-ratio:3/4;display:flex;align-items:center;justify-content:center;color:#aaa;font-size:13px;padding:20px;text-align:center">Add look image in config</div>
         <div class="bb-inspo-slide__caption">${look.caption}</div>`
    track.appendChild(slide)

    const dot = document.createElement('button')
    dot.className = 'bb-inspo-dot' + (i === 0 ? ' active' : '')
    dot.addEventListener('click', () => goToSlide(i))
    dotsWrap.appendChild(dot)
  })

  // Touch swipe
  let startX = 0
  track.addEventListener('touchstart', e => { startX = e.touches[0].clientX }, { passive: true })
  track.addEventListener('touchend',   e => {
    const diff = startX - e.changedTouches[0].clientX
    if (Math.abs(diff) > 40) goToSlide(inspoIndex + (diff > 0 ? 1 : -1))
  })

  closeBtn?.addEventListener('click', closeInspoModal)
  overlay?.addEventListener('click', e => { if (e.target === overlay) closeInspoModal() })
}

function openInspoModal() {
  document.getElementById('bb-inspo-modal-overlay')?.classList.add('active')
  document.body.style.overflow = 'hidden'
  goToSlide(0)
}

function closeInspoModal() {
  document.getElementById('bb-inspo-modal-overlay')?.classList.remove('active')
  document.body.style.overflow = ''
}

function goToSlide(i) {
  const looks = window.BB_INSPO_LOOKS || []
  inspoIndex  = Math.max(0, Math.min(i, looks.length - 1))
  const track = document.getElementById('bb-inspo-track')
  const slide = track?.querySelector('.bb-inspo-slide')
  if (!slide) return
  const slideW = slide.offsetWidth + 12 // gap
  track.style.transform = `translateX(-${inspoIndex * slideW}px)`
  document.querySelectorAll('.bb-inspo-dot').forEach((d, idx) =>
    d.classList.toggle('active', idx === inspoIndex))
}

/* ============================================================
   ZOOM
   ============================================================ */
function initZoom() {
  const overlay   = document.getElementById('bb-zoom-overlay')
  const closeBtn  = document.getElementById('bb-zoom-close')
  const zoomBtn   = document.getElementById('bb-btn-zoom')
  const composite = document.getElementById('bb-zoom-composite')

  zoomBtn?.addEventListener('click', () => {
    // Clone ALL visible layers to show the full composite
    if (composite) {
      composite.innerHTML = ''
      document.querySelectorAll('#bb-image-wrap .bb-layer').forEach(layer => {
        const clone = document.createElement('img')
        clone.className = 'bb-layer' + (layer.classList.contains('visible') ? ' visible' : '')
        clone.src = layer.src || ''
        clone.alt = ''
        composite.appendChild(clone)
      })
    }
    overlay.classList.add('active')
    document.body.style.overflow = 'hidden'
  })

  const close = () => {
    overlay.classList.remove('active')
    document.body.style.overflow = ''
  }

  closeBtn?.addEventListener('click', close)
  overlay?.addEventListener('click', e => { if (e.target === overlay) close() })
  document.addEventListener('keydown', e => { if (e.key === 'Escape') close() })
}

/* ============================================================
   INSPO
   ============================================================ */
function initInspoBtn() {
  // INSPO button opens the carousel
  document.getElementById('bb-btn-inspo')?.addEventListener('click', () => {
    openInspoModal()
  })

  document.getElementById('bb-inspo-close')?.addEventListener('click', () => {
    document.getElementById('bb-inspo-toast').classList.remove('visible')
  })

  document.getElementById('bb-inspo-cta')?.addEventListener('click', () => {
    window.location.href = BB_CONFIG.INSPO_URL
  })
}

function startInspoTimer() {
  clearTimeout(state.inspoTimer)
  state.inspoTimer = setTimeout(() => {
    if (!state.inspoShown) showInspo()
  }, BB_CONFIG.INSPO_DELAY_MS)
}

function showInspo() {
  if (state.inspoShown) return
  state.inspoShown = true
  document.getElementById('bb-inspo-toast')?.classList.add('visible')
}

/* ============================================================
   HOW IT WORKS MODAL
   ============================================================ */
function initHowItWorks() {
  document.getElementById('bb-modal-close')?.addEventListener('click', closeModal)
  document.getElementById('bb-modal-overlay')?.addEventListener('click', e => {
    if (e.target.id === 'bb-modal-overlay') closeModal()
  })
}

function openModal() {
  buildTierList()
  document.getElementById('bb-modal-overlay').classList.add('active')
  document.body.style.overflow = 'hidden'
}

function closeModal() {
  document.getElementById('bb-modal-overlay').classList.remove('active')
  document.body.style.overflow = ''
}

function buildTierList() {
  const list = document.getElementById('bb-tier-list')
  if (!list) return
  list.innerHTML = ''

  const current = getSelectedCount()

  for (let i = BB_CONFIG.DISCOUNT_THRESHOLD; i <= BB_PRODUCTS.length; i++) {
    const pct    = (i - 1) * BB_CONFIG.DISCOUNT_PER_ITEM_PERCENT
    const isNow  = i === current
    const li     = document.createElement('li')
    li.innerHTML = `
      <span class="tier-badge${isNow ? ' active' : ''}">${i} items</span>
      <span>${pct}% bundle discount${isNow ? ' ← you are here' : ''}</span>`
    list.appendChild(li)
  }
}

/* ============================================================
   SYNC SIZE / FABRIC LABELS IN PANELS
   ============================================================ */
function syncSizeFabricLabels() {
  const fabricLabel = BB_CONFIG.FABRICS[state.fabric]?.label || state.fabric || '—'

  BB_PRODUCTS.forEach(p => {
    const matEl = document.getElementById(`mat-${p.id}`)
    if (matEl) matEl.textContent = fabricLabel
    populateItemSizeDropdown(p.id)
  })
}

/* ============================================================
   SUMMARY SHEET
   ============================================================ */
function initSummary() {
  document.getElementById('bb-summary-close')?.addEventListener('click', closeSummary)
  document.getElementById('bb-summary-edit')?.addEventListener('click',  closeSummary)
  document.getElementById('bb-summary-overlay')?.addEventListener('click', e => {
    if (e.target.id === 'bb-summary-overlay') closeSummary()
  })
  document.getElementById('bb-summary-confirm')?.addEventListener('click', confirmCartAdd)
}

function openSummary() {
  const selected = BB_PRODUCTS.filter(p => state.selections[p.id])

  if (selected.length === 0) {
    showToast('Please select at least one item first.')
    return
  }

  // ── Mini bed preview — clone all visible layers into summary ──
  const previewWrap = document.getElementById('bb-summary-image-wrap')
  if (previewWrap) {
    previewWrap.innerHTML = ''
    document.querySelectorAll('.bb-layer').forEach(layer => {
      const clone = document.createElement('img')
      clone.className = 'bb-layer' + (layer.classList.contains('visible') ? ' visible' : '')
      clone.src = layer.src || ''
      clone.alt = ''
      previewWrap.appendChild(clone)
    })
  }

  // ── Item rows ──
  const itemList = document.getElementById('bb-summary-items')
  itemList.innerHTML = ''

  const fabricLabel = BB_CONFIG.FABRICS[state.fabric]?.label   || state.fabric || '—'

  selected.forEach(product => {
    const sel        = state.selections[product.id]
    const swatchColor = getSwatchColor(product, sel.colorId)
    const colorObj    = product.colors.find(c => c.id === sel.colorId)
    const isStripe    = colorObj?.swatchType === 'stripe'
    const sizeLabel   = BB_CONFIG.SIZES[getItemSize(product.id)]?.label || getItemSize(product.id)

    const li = document.createElement('li')
    li.className = 'bb-summary__item'

    // Build swatch style
    let swatchStyle = ''
    if (isStripe) {
      const [c1, c2] = colorObj.swatchColors
      swatchStyle = `background-image: repeating-linear-gradient(45deg,${c1} 0,${c1} 4px,${c2} 4px,${c2} 8px)`
    } else {
      swatchStyle = `background:${swatchColor}`
    }

    li.innerHTML = `
      <span class="bb-summary__item-dot" style="${swatchStyle}"></span>
      <div class="bb-summary__item-info">
        <div class="bb-summary__item-name">${product.title.replace('+', '')}</div>
        <div class="bb-summary__item-meta">${sel.label} &middot; ${fabricLabel} &middot; ${sizeLabel}</div>
      </div>
      ${product.isAddon
        ? `<span class="bb-summary__item-addon">${product.addonDiscount}% OFF</span>`
        : ''}`

    itemList.appendChild(li)
  })

  // ── Totals ──
  const cur                          = BB_CONFIG.CURRENCY
  const discountPct                  = getDiscountPercent()
  const { originalTotal, subtotal }  = calculateBundleTotals()
  const discountedAmt                = Math.round(subtotal * (1 - discountPct / 100))
  const saving                       = Math.round(originalTotal - discountedAmt)

  const totalsEl = document.getElementById('bb-summary-totals')
  totalsEl.innerHTML = `
    <div class="bb-summary__total-row">
      <span>Original price</span>
      <span>${cur}${Math.round(originalTotal)}</span>
    </div>
    ${discountPct > 0 ? `
    <div class="bb-summary__total-row">
      <span>Bundle discount (${discountPct}%)</span>
      <span class="saving">−${cur}${saving}</span>
    </div>` : ''}
    <div class="bb-summary__total-row grand">
      <span>Total</span>
      <span>${cur}${discountedAmt}</span>
    </div>`

  document.getElementById('bb-summary-overlay').classList.add('active')
  document.body.style.overflow = 'hidden'
}

function closeSummary() {
  document.getElementById('bb-summary-overlay').classList.remove('active')
  document.body.style.overflow = ''
}

/* ============================================================
   ADD SET TO BAG  (called from summary confirm button)
   ============================================================ */
function initAddToBag() {
  // "ADD SET TO BAG" now opens the summary first
  document.getElementById('bb-add-btn')?.addEventListener('click', openSummary)
}

async function confirmCartAdd() {
  const confirmBtn = document.getElementById('bb-summary-confirm')
  confirmBtn.textContent = 'ADDING...'
  confirmBtn.disabled    = true

  const selected = BB_PRODUCTS.filter(p => state.selections[p.id])

  const items = selected.map(product => {
    const sel = state.selections[product.id]
    return {
      id:         sel.variantId ? parseInt(sel.variantId, 10) : null,
      quantity:   1,
      properties: {
        _bundle:  'bed-builder',
        'Size':   BB_CONFIG.SIZES[getItemSize(product.id)]?.label || getItemSize(product.id),
        'Fabric': BB_CONFIG.FABRICS[state.fabric]?.label || state.fabric,
        'Colour': sel.label || sel.colorId,
      },
    }
  }).filter(i => i.id)

  if (items.length === 0) {
    console.warn('[BedBuilder] No variant IDs configured — skipping cart add.')
    window.location.href = '/cart'
    return
  }

  try {
    const res = await fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body:    JSON.stringify({ items }),
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.description || 'Cart error')
    }

    // Update header cart count
    try {
      const cart = await (await fetch('/cart.js')).json()
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count
      })
    } catch (_) {}

    const code = getDiscountCode()
    window.location.href = code ? `/checkout?discount=${code}` : '/cart'

  } catch (err) {
    console.error('[BedBuilder] Add to cart failed:', err)
    confirmBtn.textContent = 'CONFIRM & ADD TO BAG'
    confirmBtn.disabled    = false
    showToast('Something went wrong — please try again.')
  }
}